import React from "react";
import { formatDistanceToNow } from "date-fns";
import { Clock, MapPin, Phone, PhoneIncoming, User, Users } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { CallerContext, CallerFlag, CallerLastTouch, IncomingCallEvent } from "@/model/interface/IncomingCallEvent";
import { getInitialsFromFullName } from "@/util/string";

interface IncomingCallCardProps {
  event: IncomingCallEvent;
}

const FLAG_PILLS: Record<CallerFlag, { label: string; className: string }> = {
  LITIGATOR: { label: "Litigator", className: "status-pill-destructive" },
  DNC: { label: "DNC", className: "status-pill-destructive" },
  TASK_DUE_TODAY: { label: "Task due today", className: "status-pill-info" },
};

function recordLabelOf(context?: CallerContext | null): string {
  return context?.contactType && context.contactType !== "LEAD" ? "contact" : "lead";
}

function metaLineOf(event: IncomingCallEvent): string {
  if (event.contactName) {
    const typeLabel = event.context?.contactType === "LEAD" ? "Lead" : event.context?.contactType ? "Contact" : null;
    return [event.fromNumber, typeLabel].filter(Boolean).join(" · ");
  }
  return [event.callerName ? `Caller ID: ${event.callerName}` : null, event.context?.callerLocation]
    .filter(Boolean)
    .join(" · ");
}

function lastTouchText(lastTouch?: CallerLastTouch | null): string {
  if (!lastTouch) {
    return "No history yet";
  }
  const when = formatDistanceToNow(new Date(lastTouch.at), { addSuffix: true });
  if (lastTouch.channel === "SMS") {
    const opening =
      lastTouch.direction === "INBOUND"
        ? `Texted ${when}`
        : `Last text ${when}${lastTouch.byName ? ` from ${lastTouch.byName}` : ""}`;
    return [opening, lastTouch.snippet].filter(Boolean).join(" · ");
  }
  const opening = `Last call ${when}${lastTouch.byName ? ` with ${lastTouch.byName}` : ""}`;
  return [opening, lastTouch.outcome].filter(Boolean).join(" · ");
}

function ownerTextOf(context: CallerContext, currentUserId?: string): string | null {
  const recordLabel = recordLabelOf(context);
  if (context.ownerUserId && context.ownerUserId === currentUserId) {
    return `Your ${recordLabel}`;
  }
  return context.ownerName ? `${context.ownerName}'s ${recordLabel}` : null;
}

export default function IncomingCallCard({ event }: IncomingCallCardProps) {
  const { user } = useAuth();

  const context = event.context;
  const name = event.contactName ?? event.fromNumber;
  const metaLine = metaLineOf(event);
  const ownerText = context ? ownerTextOf(context, user?.id) : null;
  const flagPill = context?.flag ? FLAG_PILLS[context.flag] : null;
  const line = context?.lineName ?? context?.lineNumber;

  return (
    <div className="w-full space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="h-2 w-2 rounded-full bg-warning animate-pulse" />
        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Incoming call</span>
        {context?.teamName && (
          <span className="status-pill-info">
            <Users className="h-3 w-3" />
            {context.teamName}
          </span>
        )}
        {context && !context.lastTouch && <span className="status-pill-neutral">New caller</span>}
      </div>

      <div className="flex items-center gap-3">
        {event.contactName ? (
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
            {getInitialsFromFullName(event.contactName)}
          </span>
        ) : (
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
            <Phone className="h-4 w-4" />
          </span>
        )}
        <div className="min-w-0">
          <p className="truncate text-lg font-semibold text-foreground">{name}</p>
          {metaLine && <p className="truncate text-xs text-muted-foreground">{metaLine}</p>}
        </div>
      </div>

      {event.transferredByName && (
        <span className="status-pill-warning">
          <span className="h-1.5 w-1.5 rounded-full bg-warning" />
          Transfer from {event.transferredByName}
        </span>
      )}
      {event.transferNote && (
        <div className="space-y-1 rounded-lg bg-muted px-3 py-2">
          <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Note</p>
          <p className="text-sm">{event.transferNote}</p>
        </div>
      )}
      {event.dialerReturnCall && (
        <div className="space-y-0.5 text-sm text-muted-foreground">
          <p className="font-medium text-foreground">Calling back after your dialer call</p>
          {event.dialerReturnCall.campaignName && <p>Campaign: {event.dialerReturnCall.campaignName}</p>}
          {event.dialerReturnCall.lastDisposition && <p>Last disposition: {event.dialerReturnCall.lastDisposition}</p>}
        </div>
      )}

      {context && (
        <ul className="space-y-2 text-sm text-foreground">
          {(context.stageName || context.propertyAddress) && (
            <li className="flex min-w-0 items-center gap-2">
              <MapPin className="h-4 w-4 shrink-0 text-muted-foreground" />
              {context.stageName && (
                <span className="status-pill-neutral shrink-0">
                  <span
                    className="h-1.5 w-1.5 rounded-full bg-muted-foreground"
                    style={context.stageColor ? { backgroundColor: context.stageColor } : undefined}
                  />
                  {context.stageName}
                </span>
              )}
              {context.propertyAddress && <span className="truncate">{context.propertyAddress}</span>}
            </li>
          )}
          {line && (
            <li className="flex min-w-0 items-center gap-2">
              <PhoneIncoming className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="truncate">
                Called <span className="font-semibold">{line}</span>
                {context.lineName ? " line" : ""}
              </span>
            </li>
          )}
          <li className="flex min-w-0 items-center gap-2">
            <Clock className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="truncate">{lastTouchText(context.lastTouch)}</span>
          </li>
          {(ownerText || flagPill) && (
            <li className="flex min-w-0 items-center gap-2">
              <User className="h-4 w-4 shrink-0 text-muted-foreground" />
              {ownerText && <span className="truncate">{ownerText}</span>}
              {flagPill && <span className={`${flagPill.className} shrink-0`}>{flagPill.label}</span>}
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
